import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Play, ArrowLeft } from 'lucide-react';

const DemoArena = ({ apiService }) => {
    const navigate = useNavigate();
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);

    // Only valid UUIDs — slug IDs like 'password-security' cause extra DB fallback
    // queries on the backend and are fragile under production load.
    const DEMO_CHALLENGE_IDS = [
        '173a6ff7-f0f5-4030-ba55-4fbb15b08f75',
        '1514c468-366a-45e0-bf20-33b2b4d5a561', // The Leaked Credential Dump (replaces 'password-security' slug)
        '9835dc66-1106-436f-8d65-5b62c0256e0e',
        '39056938-d632-40ff-bdb4-919ade214e6d',
        '3d141d56-b84a-4c57-a66b-6480d602a29c',
        '96177052-e2dc-441e-9745-b6af975810ff',
        '36e64308-10fb-403a-bcc2-80496a84ac6b',
        '8723810d-15f3-4be9-8276-a72839017d2e'
        // '1d8af63f-e5ce-452d-a60b-94ec42ac072a'
    ];

    useEffect(() => {
        const fetchDemoChallenges = async () => {
            setLoading(true);
            try {
                // Try the bulk endpoint first — one DB round-trip instead of 9.
                let validChallenges = [];
                try {
                    const bulkRes = await apiService.get(
                        `/challenges/demo?ids=${DEMO_CHALLENGE_IDS.join(',')}`
                    );
                    if (bulkRes.success && Array.isArray(bulkRes.data) && bulkRes.data.length > 0) {
                        // Preserve the display order defined above
                        const map = {};
                        bulkRes.data.forEach(c => { map[c.id] = c; });
                        validChallenges = DEMO_CHALLENGE_IDS.map(id => map[id]).filter(Boolean);
                    }
                } catch (_) {
                    // Bulk endpoint not available yet — fall back to sequential individual fetches
                    // (sequential, not parallel, to avoid bursting Render's free-tier DB pool)
                    for (const id of DEMO_CHALLENGE_IDS) {
                        try {
                            const res = await apiService.get(`/challenges/${id}`);
                            if (res.success && res.data) {
                                validChallenges.push(res.data);
                            }
                        } catch (err) {
                            console.warn(`Failed to fetch demo challenge ${id}`, err);
                        }
                    }
                }

                setChallenges(validChallenges);
            } catch (error) {
                console.error('Error fetching demo challenges:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDemoChallenges();
    }, [apiService]);

    const handleStartDemo = (challenge) => {
        // Navigate with demo=true query param and pass image URL in state
        navigate(`/challenge/${challenge.id}?demo=true`, {
            state: {
                imageUrl: challenge.imageUrl || challenge.promoImageUrl
            }
        });
    };

    return (
        <div className="min-h-[100dvh] bg-black text-white font-sans flex flex-col overflow-x-hidden">
            {/* Header / Nav */}
            <div className="p-3 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 border-b border-white/10 bg-gray-900/50 backdrop-blur-md sticky top-0 z-10">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 rounded-lg p-2 text-gray-300 hover:text-white transition-colors duration-200 border border-yellow-500/30 shadow-[0_0_5px_#fbbf24,0_0_15px_#fbbf24] hover:shadow-[0_0_10px_#f59e0b]"
                >
                    <ArrowLeft size={20} />
                    Back to Dashboard
                </button>
                <h1 className="text-lg sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 sm:ml-6 text-center sm:text-left leading-tight">
                    Cyber Jugs Experience Arena
                </h1>
            </div>

            <main className="flex-1 p-3 sm:p-8 overflow-y-auto" style={{
                backgroundImage: "radial-gradient(circle at center, #1a2c4e 0%, #000000 100%)"
            }}>
                <div className="w-full px-0 sm:px-8">
                    <div className="mb-6 sm:mb-10 text-center">
                        {/* <h2 className="text-4xl font-bold mb-4">Experience Cyber Jugs</h2> */}
                        <p className="text-sm sm:text-xl text-gray-300 max-w-3xl mx-auto">
                            Try out our challenges in Demo Mode. No points will be awarded, and progress won't be saved.
                            Perfect for practice or showing off your skills!
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                        </div>
                    ) : challenges.length === 0 ? (
                        <div className="text-center p-10 bg-white/5 rounded-xl border border-white/10">
                            <Shield size={48} className="mx-auto mb-4 text-gray-500" />
                            <h3 className="text-xl font-bold text-gray-300">No Demo Challenges Available</h3>
                            <p className="text-gray-400 mt-2">Check back later for updated content.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-8">
                            {challenges.map(challenge => (
                                <div
                                    key={challenge.id}
                                    className="group relative bg-gray-800/50 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)] transition-all duration-300 flex flex-col"
                                >
                                    <div className="h-40 sm:h-48 overflow-hidden relative">
                                        <img
                                            src={challenge.imageUrl || challenge.promoImageUrl || '/assets/images/challenge-placeholder.jpg'}
                                            alt={challenge.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute top-3 right-3 bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                            DEMO MODE
                                        </div>
                                    </div>

                                    <div className="p-4 sm:p-6 flex-1 flex flex-col">
                                        <h3 className="text-base sm:text-xl font-bold mb-2 group-hover:text-blue-400 transition-colors">
                                            {challenge.title}
                                        </h3>
                                        <p className="text-gray-400 text-xs sm:text-sm mb-4 line-clamp-3 flex-1">
                                            {challenge.description}
                                        </p>

                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-auto pt-4 border-t border-white/5">
                                            <span className="text-xs font-medium px-2 py-1 rounded bg-white/10 text-gray-300 capitalize w-fit">
                                                {'Difficulty: ' + challenge.difficulty || 'Normal'}
                                            </span>
                                            <button
                                                onClick={() => handleStartDemo(challenge)}
                                                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-green-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-lg shadow-blue-900/20"
                                            >
                                                <Play size={16} fill="currentColor" />
                                                Play Demo
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default DemoArena;
